import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PipelineBuilder = ({ stages, onStageCreate, onStageSelect, onStageReorder }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newStage, setNewStage] = useState({
    stageName: '',
    color: '#3B82F6',
    icon: 'Circle'
  });

  const iconOptions = [
    'Circle', 'UserPlus', 'Phone', 'CheckCircle', 'Calendar',
    'FileText', 'MessageSquare', 'DollarSign', 'Trophy', 'Star'
  ];

  const colorOptions = [
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Purple', value: '#8B5CF6' },
    { name: 'Pink', value: '#EC4899' },
    { name: 'Orange', value: '#F59E0B' },
    { name: 'Green', value: '#10B981' },
    { name: 'Cyan', value: '#06B6D4' },
    { name: 'Red', value: '#EF4444' }
  ];

  const handleAddStage = () => {
    if (newStage?.stageName?.trim()) {
      onStageCreate({
        ...newStage,
        stageOrder: stages?.length + 1
      });
      setNewStage({ stageName: '', color: '#3B82F6', icon: 'Circle' });
      setShowAddForm(false);
    }
  };

  return (
    <div className="bg-card rounded-xl p-4 sm:p-6 border border-border">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Icon name="Workflow" size={24} className="mr-3 text-primary" />
          <h2 className="text-xl font-semibold text-foreground">Pipeline Stages</h2>
        </div>
        <Button
          iconName="Plus"
          size="sm"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          Add Stage
        </Button>
      </div>

      {/* Add Stage Form */}
      {showAddForm && (
        <div className="mb-6 p-4 bg-muted/30 rounded-lg border border-border">
          <h3 className="text-sm font-semibold text-foreground mb-4">New Stage</h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Stage Name
              </label>
              <Input
                value={newStage?.stageName}
                onChange={(e) => setNewStage({ ...newStage, stageName: e?.target?.value })}
                placeholder="e.g., Qualified Lead"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Color
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {colorOptions?.map((color) => (
                    <button
                      key={color?.value}
                      onClick={() => setNewStage({ ...newStage, color: color?.value })}
                      className={`w-10 h-10 rounded-lg border-2 transition-all ${
                        newStage?.color === color?.value
                          ? 'border-foreground scale-110'
                          : 'border-border hover:scale-105'
                      }`}
                      style={{ backgroundColor: color?.value }}
                      title={color?.name}
                    />
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Icon
                </label>
                <select
                  value={newStage?.icon}
                  onChange={(e) => setNewStage({ ...newStage, icon: e?.target?.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:ring-2 focus:ring-primary"
                >
                  {iconOptions?.map((icon) => (
                    <option key={icon} value={icon}>{icon}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAddStage} fullWidth>
                Create Stage
              </Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)} fullWidth>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Stages List */}
      <div className="space-y-3">
        {stages?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="GitBranch" size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No pipeline stages configured yet</p>
            <p className="text-sm text-muted-foreground mt-2">Click "Add Stage" to create your first stage</p>
          </div>
        ) : (
          stages?.map((stage, index) => (
            <div
              key={stage?.id}
              onClick={() => onStageSelect(stage)}
              className="flex items-center justify-between p-4 bg-muted/20 rounded-lg border border-border hover:border-primary cursor-pointer transition-all group"
            >
              <div className="flex items-center flex-1">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted text-muted-foreground font-semibold text-sm mr-3">
                  {index + 1}
                </div>
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center mr-3"
                  style={{ backgroundColor: `${stage?.color}20` }}
                >
                  <Icon name={stage?.icon} size={20} color={stage?.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-foreground truncate">
                    {stage?.stageName}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    {stage?.approvalRequired && (
                      <span className="text-xs px-2 py-0.5 bg-yellow-500/10 text-yellow-600 rounded">
                        Approval Required
                      </span>
                    )}
                    {!stage?.isActive && (
                      <span className="text-xs px-2 py-0.5 bg-red-500/10 text-red-600 rounded">
                        Inactive
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <Icon
                name="ChevronRight"
                size={20}
                className="text-muted-foreground group-hover:text-primary transition-colors"
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PipelineBuilder;